import * as React from "react";
import { DatePicker, Input, Select } from "@jhool-io/fe-components";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateProviderInfo } from "../../hooks/providers.mutations";
import {
    IEditProviderInfo,
    IProvider,
    ProviderStatus,
} from "../../types/providers.types";
import useToast from "../../../../hooks/useToast";
import { ProviderCredential } from "../../../clients/types/clients.types";
import { useFetchActiveProviders } from "../../hooks/providers.queries";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import { handleFormatDatePickerValue } from "../../../../utils/helpers";
import ListState from "../../../../components/ListState/ListState";

type Options = {
    label: React.ReactNode;
    value: string;
};
interface EditProviderProps {
    onFormSubmit: () => void;
    providerDetails?: IProvider;
}

const schema = yup.object({
    npi: yup.string(),
});

export default function EditProvider({
    onFormSubmit,
    providerDetails,
}: EditProviderProps) {
    const [providerName, setProviderName] = React.useState("");

    const [practicingStartDate, setPracticingStartDate] =
        React.useState<Date | null>(
            providerDetails?.started_practicing
                ? new Date(providerDetails?.started_practicing)
                : null
        );

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<IEditProviderInfo>({
        resolver: yupResolver(schema),
    });

    const updateProviderInfo = useUpdateProviderInfo(
        providerDetails?.provider_id as string
    );

    const { data, error, isLoading, isSuccess } =
        useFetchActiveProviders(providerName);

    const queryClient = useQueryClient();

    const { toast } = useToast();

    const supervisorsForSelect = data?.data.map((supervisor) => ({
        label: <div className="capitalize">{supervisor.provider_name}</div>,
        value: supervisor.provider_id,
    }));

    const statusOptionsForSelect = Object.values(ProviderStatus).map(
        (status) => ({
            label: <div className="capitalize">{status}</div>,
            value: status,
        })
    );
    const credentialsForSelect = Object.values(ProviderCredential).map(
        (credential) => ({
            label: credential,
            value: credential,
        })
    );

    const isSupervisorOptionsForSelect = [
        { value: true, label: "Yes" },
        { value: false, label: "No" },
    ];

    const onSubmit = (payload: IEditProviderInfo) => {
        const dataToSend = {
            ...payload,
            is_supervisor: payload.is_supervisor,
            started_practicing: payload.started_practicing
                ? handleFormatDatePickerValue(payload.started_practicing)
                : undefined,
        };

        updateProviderInfo.mutate(dataToSend, {
            onSuccess: (res) => {
                queryClient.invalidateQueries({ queryKey: ["providers"] });
                toast({
                    mode: "success",
                    message:
                        res.message || "Provider info updated successfully",
                });
                onFormSubmit();
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not update provider info at this time",
                });
            },
        });
    };

    return (
        <>
            {isLoading ? (
                <div
                    className="flex items-center justify-center h-[300px]"
                    data-testid="loader"
                >
                    <span className="mid-loader" />
                </div>
            ) : null}
            {error ? (
                <ListState
                    isError
                    errorMsg={
                        error?.response?.data?.message ||
                        "Cannot edit provider info at this time"
                    }
                    stateHelperText="Try reloading this page to solve this issue"
                    context="general"
                    cardClass="h-[300px]"
                />
            ) : null}
            {isSuccess ? (
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    id="update-provider-info"
                >
                    <div className="fg fg-space-between two flex">
                        <Input
                            {...register("npi")}
                            label="NPI"
                            placeholder="NPI"
                            defaultValue={providerDetails?.npi}
                        />
                        <Controller
                            name="status"
                            control={control}
                            defaultValue={
                                statusOptionsForSelect.find(
                                    (option) =>
                                        option.value === providerDetails?.status
                                )?.value
                            }
                            render={({ field }) => (
                                <Select
                                    label="Status"
                                    placeholder="Status"
                                    options={statusOptionsForSelect}
                                    onChange={(val) =>
                                        field.onChange((val as Options).value)
                                    }
                                    defaultValue={statusOptionsForSelect.find(
                                        (option) =>
                                            option.value ===
                                            providerDetails?.status
                                    )}
                                />
                            )}
                        />
                    </div>
                    <div className="fg fg-space-between two flex">
                        <Controller
                            name="credential"
                            control={control}
                            defaultValue={
                                credentialsForSelect.find(
                                    (option) =>
                                        option.value ===
                                        providerDetails?.credential
                                )?.value
                            }
                            render={({ field }) => (
                                <Select
                                    label="Credential"
                                    placeholder="Credential"
                                    options={credentialsForSelect}
                                    onChange={(val) =>
                                        field.onChange((val as Options).value)
                                    }
                                    defaultValue={credentialsForSelect.find(
                                        (option) =>
                                            option.value ===
                                            providerDetails?.credential
                                    )}
                                    isLongListInDialog
                                />
                            )}
                        />
                        <Controller
                            name="started_practicing"
                            control={control}
                            defaultValue={
                                providerDetails?.started_practicing || ""
                            }
                            render={({ field }) => (
                                <DatePicker
                                    label="Started Practicing on"
                                    onChange={(date) => {
                                        field.onChange(date);
                                        setPracticingStartDate(date);
                                    }}
                                    selected={practicingStartDate}
                                    hasError={!!errors.started_practicing}
                                    errorText={
                                        errors.started_practicing?.type ===
                                        "typeError"
                                            ? "invalid date value"
                                            : errors.started_practicing?.message
                                    }
                                />
                            )}
                        />
                    </div>
                    <div className="fg fg-space-between two flex">
                        <Controller
                            name="is_supervisor"
                            control={control}
                            defaultValue={
                                isSupervisorOptionsForSelect.find(
                                    (option) =>
                                        option.value ===
                                        providerDetails?.is_supervisor
                                )?.value
                            }
                            render={({ field }) => (
                                <Select
                                    label="Is a Supervisor"
                                    placeholder="Is a Supervisor"
                                    options={isSupervisorOptionsForSelect}
                                    onChange={(val) =>
                                        field.onChange(
                                            (
                                                val as {
                                                    value: boolean;
                                                    label: string;
                                                }
                                            ).value
                                        )
                                    }
                                    defaultValue={isSupervisorOptionsForSelect.find(
                                        (option) =>
                                            option.value ===
                                            providerDetails?.is_supervisor
                                    )}
                                    isLongListInDialog
                                />
                            )}
                        />

                        <Controller
                            name="supervisor_id"
                            control={control}
                            defaultValue={
                                supervisorsForSelect?.find(
                                    (option) =>
                                        option.value ===
                                        providerDetails?.supervisor_id
                                )?.value
                            }
                            render={({ field }) => (
                                <Select
                                    label="Supervisor"
                                    options={
                                        data || !error || !isLoading
                                            ? supervisorsForSelect
                                            : []
                                    }
                                    isDisabled={Boolean(error)}
                                    onInputChange={(value) =>
                                        setProviderName(value)
                                    }
                                    filterOption={() => true}
                                    isSearchable
                                    placeholder={
                                        (!data && isLoading && "Loading...") ||
                                        (error &&
                                            !isLoading &&
                                            "Error loading providers") ||
                                        (data && !isLoading && "Supervisor")
                                    }
                                    isClearable
                                    onChange={(val) => {
                                        field.onChange(
                                            val ? (val as Options).value : null
                                        );
                                    }}
                                    hasError={!!errors.supervisor_id}
                                    errorText={errors.supervisor_id?.message}
                                    // eslint-disable-next-line react/no-unstable-nested-components
                                    noOptionsMessage={() => {
                                        if (isLoading) {
                                            return <Skeleton width="100%" />;
                                        }
                                        return "No options";
                                    }}
                                    value={
                                        field.value
                                            ? supervisorsForSelect?.find(
                                                  (option) =>
                                                      option.value ===
                                                      field.value
                                              )
                                            : null
                                    }
                                    isLongListInDialog
                                />
                            )}
                        />
                    </div>
                </form>
            ) : null}
        </>
    );
}
